import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCalendar, FiMapPin } = FiIcons;

function ProjectsSection() {
  const projects = [
    {
      title: 'Modern Family Residence',
      category: 'Residential',
      location: 'Downtown District',
      duration: '8 months',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'A contemporary 4-bedroom family home featuring sustainable materials and smart home technology.'
    },
    {
      title: 'Corporate Office Complex',
      category: 'Commercial',
      location: 'Business Park',
      duration: '14 months',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'A state-of-the-art office building with modern amenities and energy-efficient systems.'
    },
    {
      title: 'Historic Building Renovation',
      category: 'Renovation',
      location: 'Heritage District',
      duration: '10 months',
      image: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Careful restoration of a 1920s building while preserving its historic character.'
    },
    {
      title: 'Luxury Condominium',
      category: 'Residential',
      location: 'Waterfront',
      duration: '12 months',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'High-end residential complex with panoramic city views and premium finishes.'
    },
    {
      title: 'Retail Shopping Center',
      category: 'Commercial',
      location: 'Suburban Plaza',
      duration: '16 months',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Modern shopping center with diverse retail spaces and customer amenities.'
    },
    {
      title: 'Kitchen & Bath Remodel',
      category: 'Renovation',
      location: 'Residential Area',
      duration: '3 months',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Complete transformation of kitchen and bathroom spaces with modern fixtures.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-secondary mb-4">
            We will provide you the best
            <br />
            and building services
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our portfolio of successful construction projects that showcase our commitment to quality and innovation.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  {project.category}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-secondary mb-3">{project.title}</h3>
                <p className="text-gray-600 mb-4 text-sm">{project.description}</p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-primary" />
                    {project.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-primary" />
                    {project.duration}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/projects"
            className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-orange-600 transition-colors duration-200 space-x-2"
          >
            <span>View All Projects</span>
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default ProjectsSection;